import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoInformationCircleOutline } from "react-icons/io5";
import Tooltip from "../../../../components/tooltip";

import EpicEditModal from "./EpicModal";
import api from "../../../../services/api";
import DebounceInput from "../../../../components/DebounceInput";
import MultiSelect from "../../../../components/MultiSelect";
import { MultiAction } from "./Composant/MultiAction";
import Loader from "../../../../components/loader";

export default ({ project, setProject }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [epics, setEpics] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    statuses: searchParams.get("statuses")?.split(",") || [
      "TODO",
      "UXUI",
      "CONTENT_WRITING",
      "READY_TO_DEV",
      "IN_PROGRESS",
      "NEED_INFO",
      "READY_FOR_QA",
      "READY_TO_RELEASE",
      "DONE",
    ],
    urgent: searchParams.get("urgent")?.split(",") || [],
    modules: searchParams.get("modules")?.split(",") || [],
  });
  const [sortBy, setSortBy] = useState({ key: "points", value: 1 });
  const [selectedEpics, setSelectedEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [isEpicEditModalOpen, setIsEpicEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEpics();
  }, [filters]);

  const fetchEpics = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/epic/search", { project_id: project._id, ...filters });
      if (!ok) return toast.error("Failed to fetch roadmaps");

      setEpics(addPoints(data));

      const newSearchParams = new URLSearchParams(searchParams);
      if (filters.statuses.length > 0) newSearchParams.set("statuses", filters.statuses.join(","));
      if (filters.urgent.length > 0) newSearchParams.set("urgent", filters.urgent.join(","));
      if (filters.modules.length > 0) newSearchParams.set("modules", filters.modules.join(","));
      setSearchParams(newSearchParams);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadmaps");
    } finally {
      setLoading(false);
    }
  };

  const handleMultipleDelete = async () => {
    try {
      if (!window.confirm("Are you sure you want to delete selected epics?")) return;

      for (const epic of selectedEpics) {
        const { ok } = await api.remove(`/epic/${epic._id}`);
        if (!ok) {
          toast.error(`Failed to delete epic ${epic._id}`);
          continue;
        }
      }
      toast.success("Epics deleted successfully");
      fetchEpics();
      setSelectedEpics([]);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete epics");
    }
  };

  const handleSave = async (itemId, field, value) => {
    try {
      const { ok, data } = await api.put(`/epic/${itemId}`, { [field]: value });
      if (!ok) return toast.error("Failed to update");

      const index = epics.findIndex((item) => item._id === itemId);
      const newEpics = [...epics];
      newEpics[index] = { ...newEpics[index], [field]: value };
      setEpics(addPoints(newEpics));
      toast.success(`${field} updated successfully`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  const addPoints = (arr1) => {
    const arr = [...arr1];
    for (let i = 0; i < arr.length; i++) {
      //= (IF(B2="XL",1,0) + IF(B2="L",2,0) + IF(B2="M",3,0) + IF(B2="S",4,0)  + IF(B2="XS",5,0))*(IF(C2="XL",5,0) + IF(C2="L",4,0) + IF(C2="M",3,0) + IF(C2="S",2,0)  + IF(C2="XS",1,0))  + (IF(D2="YES",100,0))

      let chargePoints = 0;
      if (arr[i].charge === "XL") chargePoints += 1;
      if (arr[i].charge === "L") chargePoints += 2;
      if (arr[i].charge === "M") chargePoints += 3;
      if (arr[i].charge === "S") chargePoints += 4;
      if (arr[i].charge === "XS") chargePoints += 5;

      let businessPoints = 0;
      if (arr[i].business_contribution === "XL") businessPoints += 5;
      if (arr[i].business_contribution === "L") businessPoints += 4;
      if (arr[i].business_contribution === "M") businessPoints += 3;
      if (arr[i].business_contribution === "S") businessPoints += 2;
      if (arr[i].business_contribution === "XS") businessPoints += 1;

      let basePoints = chargePoints * businessPoints;
      let urgentPoints = arr[i].urgent ? 100 : 0;
      arr[i].points = basePoints + urgentPoints;

      let days_required = 0;
      if (arr[i].charge === "XL") days_required += 20;
      if (arr[i].charge === "L") days_required += 15;
      if (arr[i].charge === "M") days_required += 10;
      if (arr[i].charge === "S") days_required += 5;
      if (arr[i].charge === "XS") days_required += 1;
      arr[i].days_required = days_required;
    }
    return arr;
  };

  return (
    <div className="space-y-1">
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="flex items-start gap-4">
          <div className="w-full flex justify-between gap-4">
            <div className="grid grid-cols-4 gap-2 w-4/5">
              <DebounceInput
                debounce={300}
                className="inline-flex justify-between items-center h-[38px] gap-4 px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                placeholder="Search by title..."
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value, page: 1 }))}
              />

              <SelectModule value={filters.modules} onChange={(e) => setFilters((f) => ({ ...f, modules: e }))} project={project} />

              <MultiSelect
                id="select-status"
                options={["TODO", "UXUI", "CONTENT_WRITING", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO", "READY_FOR_QA", "READY_TO_RELEASE", "DONE"].map((status) => ({
                  value: status,
                  label: status,
                }))}
                values={filters.statuses?.map((status) => ({ value: status, label: status })) || []}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, statuses: e.map((e) => e.value) }))}
                placeholder="Status"
              />

              <MultiSelect
                id="select-urgent"
                options={[
                  { value: "yes", label: "Yes" },
                  { value: "no", label: "No" },
                ]}
                values={filters.urgent?.map((urgent) => ({ value: urgent, label: urgent })) || []}
                onSelectedChange={(e) => setFilters((f) => ({ ...f, urgent: e.map((e) => e.value) }))}
                placeholder="Urgence"
              />
            </div>
            <div className="flex gap-4 mb-2 w-1/5">
              <button
                onClick={async () => {
                  const { ok, data } = await api.post("/epic", { project_id: project._id });
                  if (!ok) return toast.error("Failed to create epic");
                  toast.success("Epic created successfully");
                  fetchEpics();
                  setSelectedEpic(data);
                  setIsEpicEditModalOpen(true);
                }}
                className="blue-btn w-full h-[38px]">
                New Epic
              </button>
              <MultiAction selectedEpics={selectedEpics} epics={epics} project={project} handleMultipleDelete={handleMultipleDelete} />
            </div>
          </div>
        </div>
      </div>
      {isEpicEditModalOpen && (
        <EpicEditModal
          epic={selectedEpic}
          project={project}
          setProject={setProject}
          onClose={(e) => {
            if (!selectedEpic) return;
            const index = epics.findIndex((item) => item._id === selectedEpic._id);
            const newEpics = [...epics];
            newEpics[index] = { ...newEpics[index], ...e };
            setEpics(addPoints(newEpics));
            setSelectedEpic(null);
            setIsEpicEditModalOpen(false);
          }}
          onDelete={() => {
            const newEpics = epics.filter((item) => item._id !== selectedEpic._id);
            setEpics(newEpics);
            setSelectedEpic(null);
            setIsEpicEditModalOpen(false);
          }}
        />
      )}

      <div className="w-full border rounded overflow-x-auto no-scrollbar h-full bg-white">
        <table className="w-full">
          <thead className="text-left sticky top-0 z-10 bg-white shadow-sm">
            <tr className="border-b">
              <th className="p-2">
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedEpics(epics);
                      } else {
                        setSelectedEpics([]);
                      }
                    }}
                  />
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Title</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "title" ? { key: "title", value: sortBy.value * -1 } : { key: "title", value: 1 });
                    }}>
                    {sortBy.key === "title" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "title" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>

              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Tags</h3>
                </div>
              </th>

              <th className="p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold">Charge</h3>
                    <Tooltip
                      description={`XS - Quick task (2 hours or less)\nS - Half day task\nM - Full day task (1 day)\nL - Complex task (2-3 days)\nXL - Major task (3+ days)`}>
                      <IoInformationCircleOutline className="text-gray-500 text-sm cursor-help" />
                    </Tooltip>
                  </div>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "charge" ? { key: "charge", value: sortBy.value * -1 } : { key: "charge", value: 1 });
                    }}>
                    {sortBy.key === "charge" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "charge" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold">Apport Business</h3>
                    <Tooltip
                      description="XS - Minimal impact on business value
S - Minor improvement to business value
M - Moderate business value
L - High business value
XL - Critical business impact">
                      <IoInformationCircleOutline className="text-gray-500 text-sm cursor-help" />
                    </Tooltip>
                  </div>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "business_contribution" ? { key: "business_contribution", value: sortBy.value * -1 } : { key: "business_contribution", value: 1 });
                    }}>
                    {sortBy.key === "business_contribution" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "business_contribution" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Urgent</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "urgent" ? { key: "urgent", value: sortBy.value * -1 } : { key: "urgent", value: 1 });
                    }}>
                    {sortBy.key === "urgent" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "urgent" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Points</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "points" ? { key: "points", value: sortBy.value * -1 } : { key: "points", value: 1 });
                    }}>
                    {sortBy.key === "points" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "points" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
              <th className="p-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">Status</h3>
                  <button
                    className="h-4 flex flex-col justify-center"
                    onClick={() => {
                      setSortBy(sortBy.key === "status" ? { key: "status", value: sortBy.value * -1 } : { key: "status", value: 1 });
                    }}>
                    {sortBy.key === "status" && sortBy.value === 1 ? (
                      <IoIosArrowUp className="text-secondary" />
                    ) : sortBy.key === "status" && sortBy.value === -1 ? (
                      <IoIosArrowDown className="text-secondary" />
                    ) : (
                      <>
                        <IoIosArrowUp className="text-secondary" />
                        <IoIosArrowDown className="text-secondary" />
                      </>
                    )}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="relative">
            {loading ? (
              <tr>
                <td colSpan={7}>
                  <div className="py-4 flex w-full justify-center bg-gray-50">
                    <Loader />
                  </div>
                </td>
              </tr>
            ) : epics.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-4 text-center bg-gray-50">
                  No data found
                </td>
              </tr>
            ) : (
              <AnimatePresence>
                {epics
                  .sort((a, b) => {
                    if (sortBy.key === "created_at") return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
                    if (sortBy.key === "points") return sortBy.value * (b.points - a.points);
                    return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
                  })
                  .map((item, index) => {
                    return (
                      <motion.tr
                        key={item._id}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className={`${index % 2 === 0 ? "bg-gray-100" : "bg-gray-50"} cursor-pointer`}>
                        <td
                          className="border px-4 py-2 cursor-pointer"
                          onClick={(e) => {
                            if (selectedEpics.some((epic) => epic._id === item._id)) {
                              setSelectedEpics(selectedEpics.filter((epic) => epic._id !== item._id));
                            } else {
                              setSelectedEpics([...selectedEpics, item]);
                            }
                          }}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={selectedEpics.some((epic) => epic._id === item._id)}
                            onChange={() => {}}
                          />
                        </td>
                        <td
                          onClick={() => {
                            setSelectedEpic(item);
                            setIsEpicEditModalOpen(true);
                          }}
                          className="border px-4 py-2 text-sm hover:bg-gray-200">
                          <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">#{item.index}</span>
                              <span className="line-clamp-3">{item.title}</span>
                            </div>
                            {item.module && (
                              <div className="mt-1">
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{item.module}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="border px-4 py-2 max-w-xs truncate">
                          <div className="flex flex-wrap gap-2">
                            {item.tags?.map((t) => (
                              <span key={t} className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                {t}
                              </span>
                            ))}
                          </div>
                        </td>
                        <EditableSelectCell itemId={item._id} field="charge" value={item.charge} options={["TO DEFINE", "XS", "S", "M", "L", "XL"]} onSave={handleSave} />
                        <EditableSelectCell
                          itemId={item._id}
                          field="business_contribution"
                          value={item.business_contribution}
                          options={["TO DEFINE", "XS", "S", "M", "L", "XL"]}
                          onSave={handleSave}
                        />
                        <EditableSelectCell itemId={item._id} field="urgent" value={item.urgent ? "yes" : "no"} options={["yes", "no"]} onSave={handleSave}>
                          <UrgentTag item={item} />
                        </EditableSelectCell>
                        <td className="border px-4 py-2 max-w-xs truncate">{item.points}</td>
                        <EditableSelectCell
                          itemId={item._id}
                          field="status"
                          value={item.status}
                          options={["TODO", "UXUI", "CONTENT_WRITING", "READY_TO_DEV", "IN_PROGRESS", "NEED_INFO", "READY_FOR_QA", "READY_TO_RELEASE", "DONE"]}
                          onSave={handleSave}>
                          <StatusTag item={item} />
                        </EditableSelectCell>
                      </motion.tr>
                    );
                  })}
              </AnimatePresence>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EditableSelectCell = ({ itemId, field, value, options, onSave, children }) => {
  const [editing, setEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);
  const cellRef = useRef(null);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const handleSave = async (e) => {
    const newValue = e.target.value;
    setCurrentValue(newValue);
    setEditing(false);
    await onSave(itemId, field, newValue);
  };

  const handleClickOutside = (event) => {
    if (cellRef.current && !cellRef.current.contains(event.target)) {
      setEditing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <td ref={cellRef} onClick={() => setEditing(true)} onBlur={() => setEditing(false)} className="border px-4 py-2 max-w-xs truncate text-sm hover:bg-gray-200">
      {editing ? (
        <select className="input p-1" value={currentValue} onChange={handleSave}>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        children || currentValue
      )}
    </td>
  );
};

const StatusTag = ({ item }) => {
  if (!item?.status) return null;
  const colors = {
    TODO: "bg-blue-600 text-xs text-white border-gray-700",
    UXUI: "bg-purple-100 text-xs text-purple-800",
    CONTENT_WRITING: "bg-orange-100 text-xs text-orange-800",
    READY_TO_DEV: "bg-indigo-100 text-xs text-indigo-800",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800",
    NEED_INFO: "bg-yellow-100 text-xs text-yellow-800",
    READY_FOR_QA: "bg-pink-100 text-xs text-pink-800",
    READY_TO_RELEASE: "bg-emerald-100 text-xs text-emerald-800",
    DONE: "bg-green-100 text-xs text-green-800",
  };
  return <div className={`px-2 py-1 rounded w-fit ${colors[item?.status]}`}>{item?.status}</div>;
};

const UrgentTag = ({ item }) => {
  const colors = {
    true: "bg-red-600 text-xs text-white border border-red-700",
    false: "bg-green-100 text-xs text-green-800 border border-green-200",
  };
  return <div className={`px-2 py-1 rounded w-fit ${colors[item?.urgent]}`}>{item?.urgent ? "Yes" : "No"}</div>;
};

const SelectModule = ({ value, onChange, project }) => {
  const [options, setOptions] = useState([]);
  const [lastFetch, setLastFetch] = useState(0);

  async function fetchModules() {
    const { data } = await api.post("/epic/aggregate", { project_id: project._id, key: "module" });
    const arr = data.filter((e) => e._id !== null).map((e) => ({ label: e._id, value: e._id, count: e.count }));
    setOptions(arr);
    setLastFetch(Date.now());
  }

  useEffect(() => {
    // Initial fetch when component mounts
    fetchModules();
  }, [project._id]);

  // Refetch when component gains focus if more than 5 seconds have passed
  useEffect(() => {
    function handleFocus() {
      if (Date.now() - lastFetch > 5000) {
        fetchModules();
      }
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [lastFetch]);

  return (
    <MultiSelect
      id="select-modules"
      options={options}
      values={value?.map((tag) => ({ value: tag, label: tag }))}
      onSelectedChange={(e) => onChange(e.map((e) => e.value))}
      placeholder="Module"
    />
  );
};
